import {
  MembershipBadge,
  MembershipGrade,
  MembershipBadgeSize,
  ProductBadge,
} from '@yanolja-rnd/nol-design-system'
import {
  MemberClassBadge,
  TMemberClassBadge,
  TMemberClassGlobalBadge,
} from '~/api/services/member-class-api/index.type'
import { toKRW } from '~/app/utils/krNumberToString'
import Image from '~/app/components-nds/common/image'

interface Props {
  memberClass: TMemberClassBadge | TMemberClassGlobalBadge
}

const YANOLJA_IMAGE_URL = 'https://yaimg.yanolja.com/web/images/common/member-class'
/** 등급 심볼 + VIP + 회원 등급 텍스트 */
const MEMBER_CLASS_SYMBOL_VIP_GRADE = {
  [MemberClassBadge.SILVER_CLASS]: 'img_vip_silver',
  [MemberClassBadge.GOLD_CLASS]: 'img_vip_gold',
  [MemberClassBadge.PLATINUM_CLASS]: 'img_vip_platinum',
} as const

/** 등급 심볼 + 회원 등급 텍스트 */
const MEMBER_CLASS_SYMBOL_GRADE = {
  [MemberClassBadge.SILVER_CLASS]: 'img_grade_silver',
  [MemberClassBadge.GOLD_CLASS]: 'img_grade_gold',
  [MemberClassBadge.PLATINUM_CLASS]: 'img_grade_platinum',
} as const

/** 등급 심볼 */
const MEMBER_CLASS_SYMBOL = {
  [MemberClassBadge.SILVER_CLASS]: 'img_symbol_silver',
  [MemberClassBadge.GOLD_CLASS]: 'img_symbol_gold',
  [MemberClassBadge.PLATINUM_CLASS]: 'img_symbol_platinum',
} as const

export const MEMBER_CLASS_DISCOUNT = {
  [MemberClassBadge.SILVER_CLASS]: 'silver',
  [MemberClassBadge.GOLD_CLASS]: 'gold',
  [MemberClassBadge.PLATINUM_CLASS]: 'platinum',
} as const

/**
 * 공통 > 회원 등급 VIP 뱃지
 *  - 심볼 + VIP + 등급 텍스트
 */
export const MemberClassSymbolVIPGrade = ({ memberClass }: Props) => {
  const image = `${YANOLJA_IMAGE_URL}/${MEMBER_CLASS_SYMBOL_VIP_GRADE[memberClass]}.svg`

  return <Image src={image} alt={memberClass} className="h-18" />
}

/**
 * 공통 > 회원 등급 텍스트
 *  - 심볼 + 등급 텍스트
 */
export const MemberClassSymbolGrade = ({ memberClass }: Props) => {
  const image = `${YANOLJA_IMAGE_URL}/${MEMBER_CLASS_SYMBOL_GRADE[memberClass]}.svg`

  return <Image src={image} alt={memberClass} className="h-18" />
}

/**
 * 공통 > 회원 등급 심볼
 */
export const MemberClassSymbol = ({ memberClass }: Props) => {
  const icon = `${YANOLJA_IMAGE_URL}/${MEMBER_CLASS_SYMBOL[memberClass]}.svg`

  return <Image src={icon} alt={memberClass} className="h-16" />
}

interface DiscountProps {
  memberClass: TMemberClassBadge | TMemberClassGlobalBadge
  discountAmount: number
}

/**
 * 공통 > 회원 등급에 따른 할인 가격 안내
 */
export const MemberClassDiscount = ({ memberClass, discountAmount }: DiscountProps) => {
  if (memberClass === 'SILVER_CLASS' || memberClass === 'FLIGHT_ORDER') return null

  return (
    <div className="flex flex-row items-center justify-center gap-2 rounded-6 bg-opacity-black-80 px-6">
      {/* className은 바깥 wrapper에 붙고 padding(px-6 / pc:px-6)은 안쪽 div에 있어 자식 셀렉터로 제거한다 */}
      <MembershipBadge
        grade={MEMBER_CLASS_DISCOUNT[memberClass] as MembershipGrade}
        type="vip_special_price"
        size="medium"
        className="[&>div]:bg-static-black/0 [&>div]:p-0 [&>div]:pc:p-0"
      />
      <span className="text-static-white typography-caption-11-bold">
        {toKRW(discountAmount)} 할인받았어요
      </span>
    </div>
  )
}

/**
 * 공통 > 회원 등급에 따른 "VIP 특가" 뱃지
 */
export const MemberClassSpecialPriceBadge = ({
  memberClass,
  size = 'medium',
  className,
}: Props & { size?: MembershipBadgeSize; className?: string }) => {
  if (memberClass === 'SILVER_CLASS' || memberClass === 'FLIGHT_ORDER') return null

  return (
    <MembershipBadge
      grade={MEMBER_CLASS_DISCOUNT[memberClass] as MembershipGrade}
      type="vip_special_price"
      size={size}
      className={className}
    />
  )
}

/**
 * 공통 > 결제수단 > 결제수단 적립 혜택 뱃지
 * - 최대 N% 포인트 적립
 * - 현재는 NOL 머니만 노출
 */
export const MemberClassPaymentRewardBadge = ({
  memberClass,
  badgeText,
}: Props & { badgeText: string }) => {
  if (memberClass === MemberClassBadge.SILVER_CLASS) {
    return (
      <ProductBadge size="medium" type="filled_blackop">
        {badgeText}
      </ProductBadge>
    )
  }
  return (
    <MembershipBadge
      grade={MEMBER_CLASS_DISCOUNT[memberClass] as MembershipGrade}
      type="text"
      size="medium"
    >
      {badgeText}
    </MembershipBadge>
  )
}
